import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getConfirmationTokenByToken,
  getVolunteer,
  updateConfirmationStatus,
  markConfirmationTokenUsed,
} from '../services/firebase';

const DAY_LABELS = {
  'Thu-April-9':  'Thursday, April 9',
  'Fri-April-10': 'Friday, April 10',
  'Sat-April-11': 'Saturday, April 11',
  'Sun-April-12': 'Sunday, April 12',
};

const containerStyle = {
  maxWidth: '540px',
  margin: '40px auto',
  padding: '16px',
  fontFamily: 'Arial, sans-serif',
  color: '#1e293b',
};

const headerStyle = {
  background: '#1e293b', color: 'white',
  padding: '20px 28px', borderRadius: '8px 8px 0 0',
};

const bodyStyle = {
  border: '1px solid #e2e8f0', borderTop: 'none',
  padding: '28px', borderRadius: '0 0 8px 8px',
};

export default function ConfirmShift() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');

  const [pageStatus, setPageStatus] = useState('loading'); // loading | selecting | submitting | done | invalid | already-used
  const [volunteer, setVolunteer] = useState(null);
  const [shiftDetails, setShiftDetails] = useState([]);
  const [selected, setSelected] = useState(new Set()); // shiftIds the volunteer checks
  const [tokenDoc, setTokenDoc] = useState(null);

  useEffect(() => {
    if (!token) { setPageStatus('invalid'); return; }
    (async () => {
      const tok = await getConfirmationTokenByToken(token);
      if (!tok) { setPageStatus('invalid'); return; }
      if (tok.usedAt) { setPageStatus('already-used'); return; }

      const vol = await getVolunteer(tok.volunteerId);
      setVolunteer(vol);
      setTokenDoc(tok);

      const details = tok.shiftDetails && tok.shiftDetails.length > 0
        ? tok.shiftDetails
        : (tok.shiftIds || []).map(id => ({ shiftId: id, day: '', location: '', shiftTime: '' }));
      setShiftDetails(details);
      // Pre-check all shifts
      setSelected(new Set(details.map(s => s.shiftId)));
      setPageStatus('selecting');
    })();
  }, [token]);

  const toggleShift = (shiftId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(shiftId)) next.delete(shiftId);
      else next.add(shiftId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    setPageStatus('submitting');
    for (const s of shiftDetails) {
      const status = selected.has(s.shiftId) ? 'confirmed' : 'pending';
      await updateConfirmationStatus(tokenDoc.volunteerId, s.shiftId, status);
    }
    await markConfirmationTokenUsed(token);
    setPageStatus('done');
  };

  if (pageStatus === 'loading' || pageStatus === 'submitting') {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
          {pageStatus === 'loading' ? 'Loading…' : 'Saving your confirmation…'}
        </div>
      </div>
    );
  }

  if (pageStatus === 'invalid') {
    return (
      <div style={containerStyle}>
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '28px', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>✗</div>
          <h2 style={{ color: '#dc2626', marginTop: 0 }}>Invalid Link</h2>
          <p style={{ color: '#7f1d1d', margin: 0 }}>This confirmation link is not valid. Please check your email for the correct link or contact the scheduling team.</p>
        </div>
      </div>
    );
  }

  if (pageStatus === 'already-used') {
    return (
      <div style={containerStyle}>
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '28px', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
          <h2 style={{ color: '#16a34a', marginTop: 0 }}>Already Confirmed</h2>
          <p style={{ color: '#166534', margin: 0 }}>Your response for this email has already been submitted. Thank you!</p>
        </div>
      </div>
    );
  }

  if (pageStatus === 'done') {
    const confirmedCount = selected.size;
    const total = shiftDetails.length;
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>SMPW Masters Event 2026</h2>
          <p style={{ margin: '4px 0 0', opacity: 0.75, fontSize: '14px' }}>Augusta, Georgia — April 9–12, 2026</p>
        </div>
        <div style={bodyStyle}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '8px' }}>✅</div>
            <h2 style={{ color: '#16a34a', margin: '0 0 8px' }}>Response Recorded!</h2>
            {volunteer && (
              <p style={{ color: '#64748b', margin: 0 }}>
                Thank you, {volunteer.firstName}!{' '}
                {confirmedCount === total
                  ? 'All your shifts have been confirmed.'
                  : `${confirmedCount} of ${total} shift${total !== 1 ? 's' : ''} confirmed. The scheduling team has been notified of any changes.`}
              </p>
            )}
          </div>

          {shiftDetails.length > 0 && (
            <table style={{ borderCollapse: 'collapse', width: '100%', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden', marginBottom: '20px' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Status</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Day</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Location</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {shiftDetails.map(s => (
                  <tr key={s.shiftId} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '8px 12px' }}>
                      {selected.has(s.shiftId)
                        ? <span style={{ color: '#16a34a', fontWeight: 600 }}>✅ Confirmed</span>
                        : <span style={{ color: '#94a3b8' }}>— Not attending</span>}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: '13px' }}>{DAY_LABELS[s.day] || s.day}</td>
                    <td style={{ padding: '8px 12px', fontSize: '13px' }}>{s.location}</td>
                    <td style={{ padding: '8px 12px', fontSize: '13px', fontWeight: 600 }}>{s.shiftTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '14px 16px', fontSize: '14px', color: '#15803d' }}>
            <strong>Reminder:</strong> Please arrive at the Kingdom Hall at least 1 hour before your shift.<br />
            <strong>Address:</strong> 4063 Jefferson Davis Hwy, Warrenville, SC 29851
          </div>
          <p style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', color: '#94a3b8', fontSize: '12px' }}>
            SMPW Scheduling Team – Augusta Masters
          </p>
        </div>
      </div>
    );
  }

  // selecting
  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>SMPW Masters Event 2026</h2>
        <p style={{ margin: '4px 0 0', opacity: 0.75, fontSize: '14px' }}>Augusta, Georgia — April 9–12, 2026</p>
      </div>
      <div style={bodyStyle}>
        <h3 style={{ color: '#2563eb', marginTop: 0 }}>Confirm Your Attendance</h3>
        {volunteer && (
          <p style={{ marginBottom: '20px' }}>
            Hi <strong>{volunteer.firstName}</strong>, please check the shifts below that you are able to attend.
            If you cannot make a particular shift, uncheck it — the scheduling team will follow up with you.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
          {shiftDetails.map(s => (
            <label
              key={s.shiftId}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                background: selected.has(s.shiftId) ? '#f0fdf4' : '#f8fafc',
                border: `1px solid ${selected.has(s.shiftId) ? '#86efac' : '#e2e8f0'}`,
                borderRadius: '8px', padding: '12px 16px', cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(s.shiftId)}
                onChange={() => toggleShift(s.shiftId)}
                style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>
                  {DAY_LABELS[s.day] || s.day} · {s.shiftTime}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                  📍 {s.location}
                </div>
              </div>
            </label>
          ))}
        </div>

        {selected.size === 0 && (
          <p style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px' }}>
            Please select at least one shift, or contact the scheduling team if none of these work.
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={selected.size === 0}
          style={{
            width: '100%', padding: '14px',
            background: selected.size === 0 ? '#94a3b8' : '#16a34a',
            color: 'white', border: 'none', borderRadius: '6px',
            fontSize: '16px', fontWeight: 700, cursor: selected.size === 0 ? 'default' : 'pointer',
          }}
        >
          Confirm {selected.size === shiftDetails.length
            ? 'All Shifts'
            : selected.size === 0
              ? '(Select a shift)'
              : `${selected.size} of ${shiftDetails.length} Shifts`}
        </button>

        <div style={{ marginTop: '20px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '14px 16px', fontSize: '14px', color: '#15803d' }}>
          <strong>Reminder:</strong> Please arrive at the Kingdom Hall at least 1 hour before your shift.<br />
          <strong>Address:</strong> 4063 Jefferson Davis Hwy, Warrenville, SC 29851
        </div>

        <p style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', color: '#94a3b8', fontSize: '12px' }}>
          SMPW Scheduling Team – Augusta Masters
        </p>
      </div>
    </div>
  );
}
